import { ARTICLE_PICTURE_CAPTION_ATTRIBUTE } from '@bms2/shared';

/**
 * 🔒 The caption drawn under a picture in the editor (T1260; decision 1943),
 * as the reader draws it: a line of its own after the picture, inside the
 * picture's own line, so it takes that line's alignment. 🔨 It is not the
 * caption's home. The picture's attribute is, and the picture bar is its one
 * editor: the drawn line carries `contenteditable="false"` and is stripped from
 * everything the surface hands its consumer, so a stored body never keeps it.
 */
export const EDITOR_CAPTION_ATTRIBUTE = 'data-rte-caption';

/** The drawn caption as the surface serialises it; its words are text, so they hold no angle bracket. */
const DRAWN = /<span data-rte-caption="true" contenteditable="false">[^<]*<\/span>/g;

/** The markup with every drawn caption taken out, which is what the surface hands its consumer. */
export function withoutDrawnCaptions(html: string): string {
  return html.replace(DRAWN, '');
}

/** The caption a picture keeps, trimmed, or null where it keeps none. */
function captionOf(picture: Element): string | null {
  const caption = picture.getAttribute(ARTICLE_PICTURE_CAPTION_ATTRIBUTE)?.trim() ?? '';
  return caption === '' ? null : caption;
}

/**
 * Brings the drawn captions into line with the pictures' own: one after each
 * captioned picture reading its caption, none anywhere else. 🔨 The surface is
 * touched only where it differs, so typing beside a picture never rewrites the
 * line the caret stands in.
 */
export function drawCaptions(surface: HTMLElement): void {
  for (const drawn of Array.from(surface.querySelectorAll(`[${EDITOR_CAPTION_ATTRIBUTE}]`))) {
    const picture = drawn.previousSibling;
    if (!(picture instanceof HTMLImageElement) || captionOf(picture) === null) drawn.remove();
  }
  for (const picture of Array.from(surface.querySelectorAll('img'))) {
    const caption = captionOf(picture);
    if (caption === null) continue;
    const next = picture.nextSibling;
    if (next instanceof HTMLElement && next.hasAttribute(EDITOR_CAPTION_ATTRIBUTE)) {
      if (next.textContent !== caption) next.textContent = caption;
      continue;
    }
    const drawn = surface.ownerDocument.createElement('span');
    drawn.setAttribute(EDITOR_CAPTION_ATTRIBUTE, 'true');
    drawn.setAttribute('contenteditable', 'false');
    drawn.textContent = caption;
    picture.after(drawn);
  }
}
